// Demand Planning ("New Production") — the first screen. Pulls live Shopify inventory + sales
// velocity and tells you, per SKU: days of stock left, whether to order now/soon, and how much to
// produce. Feeds straight into Production Booking.
import { useCallback, useEffect, useState } from "react";
import type { DemandRow, DemandSnapshot, DemandTrigger, RunItem } from "@shared/types.ts";
import { snapProductionQty, defaultRunSizing } from "@shared/engine.ts";
import { api } from "../lib/api.ts";
import { useData } from "../state/store.tsx";
import { Badge, EmptyState, StatCard, StatGrid, SkeletonCard, InfoTip, SegTabs, Modal, LabeledInput } from "../components/ui.tsx";
import { num } from "../lib/format.ts";

const tone = (t: DemandTrigger) => (t === "ORDER NOW" ? "danger" : t === "ORDER SOON" ? "warn" : t === "SCHEDULED" ? "brand" : t === "ENOUGH STOCK" ? "ok" : "muted");
type Cat = "All" | "Drinks" | "Gummies";

export default function Demand({ onBook }: { onBook: (items: RunItem[]) => void }) {
  const { data, businessId, mutate } = useData();
  const [snap, setSnap] = useState<DemandSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cat, setCat] = useState<Cat>("All");
  const [mfrEdit, setMfrEdit] = useState<DemandRow | null>(null);

  const load = useCallback(async (refresh?: boolean) => {
    if (!businessId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    setErr(null);
    try {
      setSnap(await api.demand(businessId, refresh));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load demand");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [businessId]);

  useEffect(() => { void load(); }, [load]);

  // Amazon SP-API reports are async — trigger and poll, then refresh demand once ready.
  const [amzMsg, setAmzMsg] = useState<string | null>(null);
  const [amzBusy, setAmzBusy] = useState(false);
  const syncAmazon = useCallback(async () => {
    if (!businessId) return;
    setAmzBusy(true); setAmzMsg("Requesting Amazon reports…");
    try {
      let res = await api.amazon(businessId, true);
      if (!res.configured) { setAmzMsg("Amazon not configured for this business."); return; }
      for (let i = 0; i < 12 && !res.ready; i++) {
        await new Promise((r) => setTimeout(r, 8000));
        res = await api.amazon(businessId);
        if (res.error) { setAmzMsg(`Amazon: ${res.error}`); return; }
        setAmzMsg(res.note || "Amazon reports processing…");
      }
      if (res.ready) { setAmzMsg(`Amazon synced (${res.skus ?? 0} SKUs)`); await load(true); }
      else setAmzMsg("Amazon still processing — try again shortly.");
    } catch (e) {
      setAmzMsg(e instanceof Error ? e.message : "Amazon sync failed");
    } finally {
      setAmzBusy(false);
    }
  }, [businessId, load]);

  if (!data) return null;
  const p = data.business.planning;
  const hasAmazon = (data.business.channels || []).some((c) => c.kind === "amazon" && c.auto);
  const hasGummies = (snap?.rows || []).some((r) => r.category === "Gummies");

  // Manufacturer/co-packer finished stock (e.g. "Stock at UBC") is folded into the total by the
  // demand engine (server-side). Editing it saves on the business and refreshes demand.
  const setMfr = async (skuId: string, v: number) => {
    await mutate((draft) => {
      const ms = { ...(draft.business.manufacturerStock || {}) };
      if (v > 0) ms[skuId] = v; else delete ms[skuId];
      draft.business.manufacturerStock = ms;
    });
    await load();
  };
  const allRows: DemandRow[] = snap?.rows || [];
  const rows = allRows.filter((r) => cat === "All" ? true : cat === "Gummies" ? r.category === "Gummies" : r.category !== "Gummies");
  const needNow = rows.filter((r) => r.trigger === "ORDER NOW");
  const needSoon = rows.filter((r) => r.trigger === "ORDER SOON");
  const skuName = (id: string) => data.skus.find((s) => s.id === id)?.name || id;

  // Manufactured SKUs snap to a valid production run size (per SKU); purchased items (gummies) are
  // ordered from a supplier, so they keep the raw need.
  const sizing = data.business.runSizing || defaultRunSizing(data.business.market);
  const produceQty = (r: DemandRow) =>
    r.howMuchToOrder > 0 && !r.purchased ? snapProductionQty(r.howMuchToOrder, sizing) : r.howMuchToOrder;

  const bookAll = () => {
    const items: RunItem[] = [...needNow, ...needSoon].filter((r) => r.howMuchToOrder > 0).map((r) => ({ skuId: r.skuId, cans: produceQty(r) }));
    if (items.length) onBook(items);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">Demand Planning — What to produce &amp; when</h2>
          <p className="text-muted text-xs mt-0.5">
            Live from Shopify (last {snap?.windowDays ?? 90}d sales).
            {p && ` Order-now under ${p.leadTimeDays + p.safetyStockDays}d cover; each production covers ${p.runwayDays ?? 150}d of sales.`}
            {snap?.at ? ` · updated ${new Date(snap.at).toLocaleString()}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(needNow.length > 0 || needSoon.length > 0) && <button className="btn-primary btn-sm" onClick={bookAll}>Book all to order →</button>}
          {hasAmazon && <button className="btn-sm border border-edge hover:bg-paper" disabled={amzBusy} onClick={syncAmazon}>{amzBusy ? "Syncing Amazon…" : "Sync Amazon"}</button>}
          <button className="btn-sm border border-edge hover:bg-paper" disabled={refreshing} onClick={() => load(true)}>{refreshing ? "Refreshing…" : "Refresh"}</button>
        </div>
      </div>

      {hasGummies && <SegTabs tabs={[{ id: "All", label: "All" }, { id: "Drinks", label: "Drinks" }, { id: "Gummies", label: "Gummies" }]} value={cat} onChange={setCat} />}
      {amzMsg && <div className="card border-brand/30 bg-brandSoft text-brand text-xs">{amzMsg}</div>}
      {err && <div className="card border-danger/30 bg-danger/5 text-danger text-sm">{err}</div>}

      {loading && !snap ? (
        <SkeletonCard lines={6} />
      ) : snap && !snap.configured ? (
        <div className="card"><EmptyState title="No channels configured" sub="Add a sales channel / warehouse in Admin → Channels (Shopify or Amazon can be synced; others are manual)." /></div>
      ) : (
        <>
          <StatGrid>
            <StatCard label="Order now" value={num(needNow.length)} tone={needNow.length ? "danger" : undefined} sub="below safety cover" />
            <StatCard label="Order soon" value={num(needSoon.length)} tone={needSoon.length ? "warn" : undefined} />
            <StatCard label="Healthy" value={num(rows.filter((r) => r.trigger === "ENOUGH STOCK").length)} tone="ok" />
            <StatCard label="Units to produce" value={num([...needNow, ...needSoon].reduce((a, r) => a + produceQty(r), 0))} accent />
          </StatGrid>

          {snap?.error && <div className="card border-warn/30 bg-warn/5 text-warn text-xs">Shopify: {snap.error} (showing last cached values)</div>}

          <div className="card overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr>
                <th className="th">SKU</th>
                <th className="th text-right">Inventory <InfoTip text="Sellable stock across all connected channels." /></th>
                <th className="th text-right">At mfr <InfoTip text="Finished goods held at the manufacturer/co-packer (e.g. Stock at UBC) — not in Shopify. Editable; counts toward total inventory." /></th>
                <th className="th text-right">Total <InfoTip text="Total inventory = sellable (channels) + at manufacturer. This drives days-left and produce." /></th>
                <th className="th text-right">Incoming <InfoTip text="Units already in the production pipeline (booked / in-progress runs, not yet delivered)." /></th>
                <th className="th text-right">Sold / mo</th>
                <th className="th text-right">Days left <InfoTip text="Inventory ÷ daily sales velocity (all connected channels)." /></th>
                <th className="th">Status</th>
                <th className="th text-right">Produce / order <InfoTip text={p ? `A standard batch = ${p.runwayDays ?? 150} days of runway × the SKU's sales velocity, snapped to a valid run size. Gummies are ordered (purchased), not manufactured.` : "suggested run size"} /></th>
                <th className="th text-right"></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.skuId} className={r.trigger === "NO DATA" ? "opacity-55" : ""}>
                    <td className="td font-medium">{skuName(r.skuId)}<span className="text-muted text-[11px] ml-1">{r.shopifySku}</span></td>
                    <td className="td text-right tabular-nums">{num(r.inventory)}</td>
                    <td className="td text-right">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <span className="tabular-nums">{r.manufacturerStock ? num(r.manufacturerStock) : "—"}</span>
                        <button className="text-muted hover:text-ink" title="Edit manufacturer stock" onClick={() => setMfrEdit(r)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                        </button>
                      </span>
                    </td>
                    <td className="td text-right tabular-nums font-semibold">{num(r.inventory + (Number(r.manufacturerStock) || 0))}</td>
                    <td className="td text-right tabular-nums">{r.incoming > 0 ? <span className="text-brand">+{num(r.incoming)}</span> : "—"}</td>
                    <td className="td text-right tabular-nums">{num(r.monthlyUnits)}</td>
                    <td className="td text-right tabular-nums">{r.daysRemaining == null ? "—" : Math.round(r.daysRemaining)}</td>
                    <td className="td"><Badge tone={tone(r.trigger)}>{r.trigger}</Badge></td>
                    <td className="td text-right tabular-nums font-semibold">
                      {r.howMuchToOrder > 0 ? (
                        <span>{num(produceQty(r))}
                          {produceQty(r) !== r.howMuchToOrder && <span className="block text-[10px] font-normal text-muted">need {num(r.howMuchToOrder)}</span>}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="td text-right">{r.howMuchToOrder > 0 && <button className="btn-sm border border-edge hover:bg-paper" onClick={() => onBook([{ skuId: r.skuId, cans: produceQty(r) }])}>{r.purchased ? "Order" : "Book"}</button>}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td className="td text-muted" colSpan={10}>No SKUs mapped to a store SKU. Add a Shopify SKU to each product in Admin → SKUs.</td></tr>}
              </tbody>
              {rows.length > 0 && (
                <tfoot><tr className="border-t-2 border-edge">
                  <td className="td font-semibold">Total</td>
                  <td className="td text-right font-semibold tabular-nums">{num(rows.reduce((a, r) => a + r.inventory, 0))}</td>
                  <td className="td text-right font-semibold tabular-nums">{num(rows.reduce((a, r) => a + (Number(r.manufacturerStock) || 0), 0))}</td>
                  <td className="td text-right font-semibold tabular-nums">{num(rows.reduce((a, r) => a + r.inventory + (Number(r.manufacturerStock) || 0), 0))}</td>
                  <td className="td text-right font-semibold tabular-nums">{num(rows.reduce((a, r) => a + r.incoming, 0))}</td>
                  <td className="td" colSpan={3}></td>
                  <td className="td text-right font-semibold tabular-nums">{num(rows.reduce((a, r) => a + produceQty(r), 0))}</td>
                  <td className="td"></td>
                </tr></tfoot>
              )}
            </table>
          </div>
          <p className="text-xs text-muted">“Book” sends the suggested quantity to Booking, where you confirm the run and generate the ingredient order.</p>
        </>
      )}

      {mfrEdit && (
        <MfrModal name={mfrEdit.name} value={Number(mfrEdit.manufacturerStock) || 0}
          onClose={() => setMfrEdit(null)}
          onSave={(v) => { void setMfr(mfrEdit.skuId, v); setMfrEdit(null); }} />
      )}
    </div>
  );
}

function MfrModal({ name, value, onClose, onSave }: { name: string; value: number; onClose: () => void; onSave: (v: number) => void }) {
  const [v, setV] = useState(value ? String(value) : "");
  return (
    <Modal title={`Stock at manufacturer · ${name}`} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Finished units held at manufacturer" hint="Finished goods at the co-packer, not in Shopify. Counts toward total inventory.">
          <input autoFocus type="number" min={0} className="input" value={v} onChange={(e) => setV(e.target.value)} />
        </LabeledInput>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" onClick={() => onSave(Number(v) || 0)}>Save</button>
        </div>
      </div>
    </Modal>
  );
}
